import { useMemo } from 'react';
import type { ReactNode } from 'react';
import type { EChartsOption } from 'echarts';
import { Header, type EyebrowTone, type TitleAccent } from './_shared';
import { useECharts } from '../charts/useECharts';
import { SN_PIE_PALETTE, SN_RADAR_PALETTE, SN_GAUGE, SN_CHART_DARK } from '../charts/snEchartsTheme';

export type ChartSeries = {
  /** Legend label. */
  name: string;
  /** One value per x-axis category, same length/order as `categories`. */
  data: readonly number[];
};

export type ChartKind = 'line' | 'area' | 'bar-stacked' | 'pie' | 'radar' | 'gauge';

export type ChartSlideProps = {
  eyebrow: ReactNode;
  eyebrowTone?: EyebrowTone;
  title: ReactNode;
  titleAccent?: TitleAccent;
  sub?: ReactNode;
  kind: ChartKind;
  /** X-axis category labels (e.g. months); slice/axis labels for pie/radar. */
  categories: readonly string[];
  /** One entry per series; up to 3 render on-palette (cartesian/radar). Pie uses series[0]. */
  series: readonly ChartSeries[];
  /** Optional Y-axis name (e.g. "Active users"). */
  yName?: string;
  /** Radar axis max (radar only). Defaults to the largest series value. */
  max?: number;
  /** Gauge percentage 0–100 (gauge only). */
  value?: number;
  /** Gauge centered label (gauge only), e.g. "Active users". */
  centerLabel?: string;
  /** Gauge shape (gauge only). 'full' (default) or 'half' (180°). */
  gaugeShape?: 'full' | 'half';
  /** Multi-ring gauge (gauge only): one concentric ring per entry, outermost first. */
  rings?: readonly { value: number; label?: string }[];
  /** Set 'dark' when the slide theme is 'dark' so axis/legend colors invert. */
  surface?: 'light' | 'dark';
};

function buildOption(
  kind: ChartKind,
  categories: readonly string[],
  series: readonly ChartSeries[],
  yName?: string,
  max?: number,
  value?: number,
  centerLabel?: string,
  gaugeShape?: 'full' | 'half',
  rings?: readonly { value: number; label?: string }[],
  surface?: 'light' | 'dark',
): EChartsOption {
  if (kind === 'pie') {
    return {
      color: SN_PIE_PALETTE,
      tooltip: { trigger: 'item' },
      legend: { orient: 'vertical', right: 0, top: 'middle' },
      series: [
        {
          type: 'pie',
          radius: ['45%', '72%'],
          center: ['40%', '54%'],
          label: { show: false },
          data: categories.map((c, i) => ({ name: c, value: series[0]?.data[i] ?? 0 })),
        },
      ],
    };
  }

  if (kind === 'radar') {
    const allValues = series.flatMap((s) => [...s.data]);
    const radarMax = max ?? Math.max(100, ...allValues);
    return {
      color: SN_RADAR_PALETTE,
      tooltip: {},
      legend: { right: 0, top: 0 },
      radar: {
        radius: '62%',
        indicator: categories.map((c) => ({ name: c, max: radarMax })),
      },
      series: [
        {
          type: 'radar',
          data: series.map((s) => ({ name: s.name, value: [...s.data] })),
          areaStyle: { opacity: 0.1 },
        },
      ],
    };
  }

  if (kind === 'gauge') {
    const half = gaugeShape === 'half';
    const start = half ? 180 : 90;
    const end = half ? 0 : -270;
    const ringData = rings && rings.length ? rings : [{ value: value ?? 0, label: centerLabel }];
    const n = ringData.length;
    return {
      series: ringData.map((r, i) => ({
        type: 'gauge',
        startAngle: start,
        endAngle: end,
        radius: `${75 - i * (n > 1 ? 14 : 0)}%`,
        pointer: { show: false },
        progress: { show: true, width: 14, roundCap: true, itemStyle: { color: SN_PIE_PALETTE[i % SN_PIE_PALETTE.length] } },
        axisLine: { lineStyle: { width: 14, color: [[1, SN_GAUGE.track]] } },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        anchor: { show: false },
        title:
          i === n - 1
            ? { show: true, offsetCenter: [0, half ? '-10%' : '-12%'], fontSize: 14, color: SN_GAUGE.label, fontFamily: 'Funnel Sans, sans-serif' }
            : { show: false },
        detail:
          i === 0
            ? { valueAnimation: true, formatter: '{value}%', offsetCenter: [0, half ? '20%' : '12%'], fontSize: 36, fontWeight: 500, fontFamily: 'Funnel Display, sans-serif', color: SN_GAUGE.value }
            : { show: false },
        data: [{ value: r.value, name: r.label ?? '' }],
      })),
    };
  }

  const isBar = kind === 'bar-stacked';
  const dark = surface === 'dark';
  return {
    grid: { left: 56, right: 16, top: 40, bottom: 36, containLabel: true },
    legend: { right: 0, top: 0, textStyle: dark ? { color: SN_CHART_DARK.legend } : undefined },
    tooltip: { trigger: 'axis' },
    xAxis: {
      type: 'category',
      data: categories as string[],
      boundaryGap: isBar,
      axisLabel: dark ? { color: SN_CHART_DARK.axisLabel } : undefined,
      axisLine: dark ? { lineStyle: { color: SN_CHART_DARK.axisLine } } : undefined,
    },
    yAxis: {
      type: 'value',
      name: yName,
      axisLabel: dark ? { color: SN_CHART_DARK.axisLabel } : undefined,
      splitLine: dark ? { lineStyle: { color: SN_CHART_DARK.splitLine } } : undefined,
    },
    series: series.map((s) => ({
      name: s.name,
      type: isBar ? 'bar' : 'line',
      stack: isBar ? 'total' : undefined,
      areaStyle: kind === 'area' ? { opacity: 0.15 } : undefined,
      data: s.data as number[],
    })),
  };
}

export function ChartSlide({
  eyebrow,
  eyebrowTone,
  title,
  titleAccent,
  sub,
  kind,
  categories,
  series,
  yName,
  max,
  value,
  centerLabel,
  gaugeShape,
  rings,
  surface,
}: ChartSlideProps) {
  const option = useMemo(
    () => buildOption(kind, categories, series, yName, max, value, centerLabel, gaugeShape, rings, surface),
    [kind, categories, series, yName, max, value, centerLabel, gaugeShape, rings, surface],
  );
  const chartRef = useECharts(option);
  return (
    <div className="slide-body">
      <Header
        eyebrow={eyebrow}
        eyebrowTone={eyebrowTone}
        title={title}
        titleAccent={titleAccent}
        sub={sub}
      />
      <div className="sn-work">
        <div className="sn-chart" ref={chartRef} />
      </div>
    </div>
  );
}
